import * as utils from "../helpers/utils"
import AxiosInstance from "./AxiosInstance"
import {
	IExpenseDocument,
	EApproveType,
	IPosition,
	IPunchBundle,
	WorkPoolEmployeeSearchParams,
	ISchedulePatternTemplate,
	IUserDocument,
	EClientActiveStatus,
	IJobSearchParams,
	ISchedulePreference,
	EPayrollType,
	IQuestionDocument,
	IExpenseTemplate,
	IMassJobCreation,
	IMassJobEdit,
	IJobDocument,
	ICustomReportDocument,
	IActionPunches,
	EOTEmployeeType,
	ERecordSearchFilter,
	EJobDashboardCircleChartType,
	IUserFilter,
	IDeviceReportParameters,
	IEmployeesReportParameters,
	IJobReportParameters,
	IFavoriteDocument,
	IFavoriteParams,
	IEmployeeFileSearchParams,
	IFileDocument,
	ITimecardAdhocExportRequest,
	INoteType,
	ECustomReportLocation,
	INewCustomReportParameters,
	IScheduleDocument,
	IResourcePlannerScheduleFilter,
	IJobFilter,
	IPayrollPayGroupData,
	ITimeoffNote,
	IUserPayrollData,
	IExpenseBundle,
	ELanguage,
	IRuleProperty,
	IPunchExceptionFilterDto,
	EExceptionReviewDialogExceptionType,
	EExceptionType,
	ECustomReportType,
	ITimeoffFilter,
	IApprovalValidation,
	IUpdatePayrollSettings,
	IDate
} from "../models"
import { IScheduledJob } from "../models/data/account/settings/IScheduledJob"
import AccountStore from "../stores/AccountStore"
import AuthenticationStore from "../stores/AuthenticationStore"
import { IGigPoolDocument } from "../models/documents/GigPoolDocument"
import { IMessageGroupSend, ISuggestedMessagePrompt } from "../models/documents/MessageGroupDocument"
import { AxiosResponse } from "axios"
import { IUserReportDocument } from "../models/data/report/IUserReportDocument"
import { IWorkHistoryDay } from "../models/data/punch/WorkHistoryDay"

let axiosInstance = new AxiosInstance().instance

function setProxy() {
	axiosInstance.interceptors.request.use(config => {
		const proxy = localStorage.getItem("findd_proxy")
		if (proxy !== null) {
			if (AccountStore.getProxyAccountId() && proxy !== AccountStore.getProxyAccountId()) {
				console.log(`AccountStore.getProxyAccountId(): ${AccountStore.getProxyAccountId()}, proxy: ${proxy}`)
				//Don't allow any further API calls. We are in a bad state.
				axiosInstance = null
				AuthenticationStore.setCrossProxyError()
			}
		}
		if (AccountStore.getCurrentAccount()) {
			config.headers["proxy"] = proxy
		}
		return config
	})
}

setProxy()

export function addCustomReport(customReportDoc: ICustomReportDocument) {
	return axiosInstance.post("/custom_report", customReportDoc)
}

export function updateCustomReport(customReportDoc: ICustomReportDocument) {
	return axiosInstance.put("/custom_report", customReportDoc)
}

export function getAccountCustomReports() {
	return axiosInstance.get("/custom_report")
}

export function getCustomReportTypes(customReportLocation: ECustomReportLocation) {
	return axiosInstance.get(`/custom_report/custom_report_types?customReportLocation=${customReportLocation}`)
}

export function getCustomReportsByType(customReportType: ECustomReportType) {
	return axiosInstance.get(`/custom_report/custom_report_by_type?customReportType=${customReportType}`)
}

export function deleteCustomReports(customReportDocIds: string[]) {
	return axiosInstance.delete("/custom_report", { data: customReportDocIds })
}

export function compileCheckCustomReportDoc(customReportDoc: ICustomReportDocument) {
	return axiosInstance.post(`/custom_report/compile`, customReportDoc)
}

export function getUserDirectReports(userId) {
	return axiosInstance.get(`/users/${userId}/directreports`)
}

export function requestForgotPassword(email) {
	return axiosInstance.get(`/users/forgot_password?email=${encodeURIComponent(email)}`)
}

export function checkResetPageValide(token) {
	return axiosInstance.get("/users/checkvalidatereset/" + token)
}

export function resetPassword(token, password, reset) {
	return axiosInstance.get(`/users/reset?reset=${reset}&token=${token}&password=${encodeURIComponent(password)}`)
}

export function login(email, password) {
	return axiosInstance.post("/users/login", {
		Email: email,
		Password: password
	})
}

export function getSSOAuthData(token) {
	return axiosInstance.get("/users/ssoAuthData/" + token)
}

export function getAuthData(userId) {
	return axiosInstance.get("/users/" + userId + "/authData?proxy=" + AccountStore.getProxyAccountId())
}

export function getAuthDataByCookie() {
	return axiosInstance.get("/users/authData?proxy=" + AccountStore.getProxyAccountId())
}

export function getUser(userId) {
	return axiosInstance.get(`/users/${userId}?byMobile=false`)
}

export function addPunch(userId, punchBundle: IPunchBundle, ignoreNotAllowedPunchSetting: boolean, isWebPunch: boolean, ipAddress: string, allowPriorPeriodChange: boolean, byEmployee: boolean) {
	return axiosInstance.post(`/punches/${userId}?ignoreNotAllowedPunchSetting=${ignoreNotAllowedPunchSetting}&isWebPunch=${isWebPunch}&ipAddress=${ipAddress}&manualAdd=true&allowPriorPeriodChange=${allowPriorPeriodChange}&byEmployee=${byEmployee}`, punchBundle)
}

export function addGlobalPunch(userIds: string[], punchBundle: IPunchBundle, asyncSessionId: string) {
	return axiosInstance.post(`/punches/add_global?asyncSessionId=${asyncSessionId}`, { userIds, punchBundle })
}

export function getGlobalPunches(userIds: string[], startDateTime: string, endDateTime: string) {
	return axiosInstance.post(`/punches/get_global_punches?startDateTime=${utils.escapeDateTime(startDateTime)}&endDateTime=${utils.escapeDateTime(endDateTime)}`, userIds)
}

export function editGlobalPunch(punchBundles: IPunchBundle[], asyncSessionId: string) {
	return axiosInstance.post(`/punches/edit_global?asyncSessionId=${asyncSessionId}`, punchBundles)
}

export function getUserI9(userId) {
	return axiosInstance.get("/users/" + userId + "/i9")
}

export function getUserTimecards(userId, referenceDate) {
	return axiosInstance.get("/users/" + userId + "/timecard?includePunchMetaData=false&referenceDate=" + utils.escapeDateTime(referenceDate))
}

export function getUserTimecardFullData(userId, referenceDate, virtualTimecard, filterByJobId) {
	return axiosInstance.get(`/users/${userId}/timecard_full_data?referenceDate=${utils.escapeDateTime(referenceDate)}&virtualTimecard=${virtualTimecard}&filterByJobId=${filterByJobId}`)
}
export function setTimecardModifications(userId, timecardId, allowModifications, referenceDate) {
	return axiosInstance.get(`/users/${userId}/timecard_modifications?timecardId=${timecardId}&allowModifications=${allowModifications}&referenceDate=${utils.escapeDateTime(referenceDate)}`)
}

export function generateUserTimecard(userId) {
	return axiosInstance.get(`/users/${userId}/generate_timecard`)
}

export function exportTimecardDiff(userId, diffTimecardId, referenceDate, reasonNote) {
	return axiosInstance.get(`/users/${userId}/export_timecard_diff?diffTimecardId=${diffTimecardId}&referenceDate=${encodeURIComponent(referenceDate)}&reasonNote=${reasonNote}`)
}

export function getUserEditablePunchData(userId: string, startDateTime: string, endDateTime: string) {
	return axiosInstance.get(`/punches/get_user_editable_punch_data?userId=${userId}&startDateTime=${utils.escapeDateTime(startDateTime)}&endDateTime=${utils.escapeDateTime(endDateTime)}`)
}

export function handleActionPunches(userId: string, actionPunches: IActionPunches) {
	return axiosInstance.post(`/punches/action_punches?userId=${userId}`, actionPunches)
}

export function getI9(i9Id) {
	return axiosInstance.get("/i9/" + i9Id)
}

export function getUserHint(cellPhone) {
	return axiosInstance.get("/users/user_hint?phone=" + cellPhone)
}

export function getUserStats(userId) {
	return axiosInstance.get("/users/" + userId + "/stats")
}

export function getUserDevices(userId) {
	return axiosInstance.get("/users/" + userId + "/devices")
}

export function addUser(user) {
	return axiosInstance.post("/users", user)
}

export function deleteUser(userId) {
	return axiosInstance.delete("/users/" + userId)
}

export function addUserDevice(userId, device) {
	return axiosInstance.post("/users/" + userId + "/devices", device)
}

export function deleteLocationDevice(locationId, deviceId) {
	return axiosInstance.delete("/locations/" + locationId + "/devices/" + deviceId)
}

export function deleteUserDevice(userId, deviceId) {
	return axiosInstance.delete("/users/" + userId + "/devices/" + deviceId)
}

export function patchDevice(device) {
	return axiosInstance.patch("/devices/" + device.Id, device)
}

export function patchJob(jobId: string, job: IJobDocument) {
	return axiosInstance.patch(`/jobs/${jobId}`, job)
}

export function loadImageData(imgUrl) {
	return axiosInstance.get(imgUrl)
}

export function getPunchDetail(punchId, catchNearLocations = false) {
	return axiosInstance.get(`/punches/${punchId}?catchNearLocations=${catchNearLocations}`)
}

export function getPunchPosition(punchId) {
	return axiosInstance.get(`/punches/${punchId}/punch_position`)
}

export function getPunchBiometricExceptionDetail(punchId) {
	return axiosInstance.get("/punches/" + punchId + "/biometric-exception")
}

export function updatePunchBiometricException(punchId, biometricException) {
	return axiosInstance.put("/punches/" + punchId + "/biometric-exception", biometricException)
}

export function updatePunchLocationException(punchId, locationException) {
	return axiosInstance.put("/punches/" + punchId + "/location-exception", locationException)
}

export function getPunchLocationExceptionDetail(punchId) {
	return axiosInstance.get("/punches/" + punchId + "/location-exception")
}

export function emailExists(email) {
	return axiosInstance.get("/users/email_exists?email=" + encodeURIComponent(email))
}

export function userEmailExists(userId: string, email: string) {
	return axiosInstance.get(`/users/${userId}/user_email_exists?email=${encodeURIComponent(email)}`)
}

export function updateUser(userId: string, user: IUserDocument) {
	user.UserModified = true
	return axiosInstance.put("/users/" + userId, user)
}

export function patchUser(userId, user, userModified = true) {
	user.UserModified = userModified
	return axiosInstance.patch("/users/" + userId, user)
}

export function updatePayrollSettings(userId, updatePayrollSettings: IUpdatePayrollSettings) {
	return axiosInstance.post(`/users/${userId}/update_payroll_settings`, updatePayrollSettings)
}

export function readValidationMessage(userId: string, validationMessageId: string, referenceDate: Date): Promise<AxiosResponse<IApprovalValidation>> {
	return axiosInstance.put(`/users/${userId}/read_validation_message/${validationMessageId}?referenceDate=${encodeURIComponent(referenceDate as unknown as string)}`)
}

export function getUserRecords(userId: string, offset: number, limit: number, recordSearchFilter: ERecordSearchFilter) {
	return axiosInstance.get(`/users/${userId}/records?page=${offset / limit}&limit=${limit}&recordSearchFilter=${recordSearchFilter}`)
}

export function getUserDayRecords(userId, day) {
	return axiosInstance.get(`/users/${userId}/day_records?day=${day}`)
}

export function deleteFaceSpaces(faceSpaceIds) {
	return axiosInstance.put("/facespaces/delete", faceSpaceIds)
}

export function getAdminExceptions() {
	return axiosInstance.get("punches/biometric-exceptions")
}

export function updateAdminExceptions(exceptions) {
	return axiosInstance.post("punches/biometric-exceptions", exceptions)
}

export function loadSection1Data(shortCode) {
	return axiosInstance.get("i9/section1/" + shortCode)
}

export function patchSection1Data(i9Id, i9Partial) {
	return axiosInstance.patch("/i9/" + i9Id + "/section1", i9Partial)
}

export function patchI9(i9Id, i9Partial) {
	return axiosInstance.patch("/i9/" + i9Id, i9Partial)
}

export function postGovernmentDoc(i9Id, doc) {
	return axiosInstance.post("/i9/" + i9Id + "/documents", doc)
}

export function getI9Pdf(i9Id) {
	return axiosInstance.get("/i9/" + i9Id + "/pdf")
}

export function patchPunch(punchId: string, updatePunchPosition: boolean, punchBundle, allowPriorPeriodChange: boolean, byEmployee: boolean) {
	return axiosInstance.patch(`/punches/${punchId}?updatePunchPosition=${updatePunchPosition}&allowPriorPeriodChange=${allowPriorPeriodChange}&byEmployee=${byEmployee}`, punchBundle)
}

export function deletePunch(punchId: string, allowPriorPeriodChange: boolean, byEmployee: boolean) {
	return axiosInstance.delete(`/punches/${punchId}?allowPriorPeriodChange=${allowPriorPeriodChange}&byEmployee=${byEmployee}`)
}

//More than just removing the position is occuring in the API, so don't do this delete client-side
export function removeUserPositions(userId, positionIds) {
	return axiosInstance.post("/users/" + userId + "/positions/delete_position_list", positionIds)
}

export function addEquipment(equipment) {
	return axiosInstance.post("/equipment", equipment)
}

export function updateEquipment(equipment) {
	return axiosInstance.put("/equipment", equipment)
}

export function deleteEquipment(equipmentId) {
	return axiosInstance.delete("/equipment/" + equipmentId)
}

export function getEquipment(equipmentId) {
	return axiosInstance.get("/equipment/" + equipmentId)
}

export function getEquipmentTimecard(equipmentId, referenceDate) {
	return axiosInstance.get("/equipment/" + equipmentId + "/timecards?referenceDate=" + utils.escapeDateTime(referenceDate))
}

export function generateEquipmentTimecard(equipmentId) {
	return axiosInstance.get(`equipment/${equipmentId}/generate_timecard`)
}

export function getDevice(deviceId) {
	return axiosInstance.get(`/devices/${deviceId}?preserveOriginalHierarchyId=true`)
}

export function addDevice(device) {
	return axiosInstance.post("/devices", device)
}

export function removeDevice(deviceId) {
	return axiosInstance.delete("/devices/" + deviceId)
}

export function getAsyncAuthString(userId) {
	return axiosInstance.get("/users/" + userId + "/async_auth")
}

export function getPayrollTimeReportDataDownload(accountId, levelIdFilter, payGroupIds: string[], referenceDate, asyncSessionId) {
	return axiosInstance.post(
		"/reportingv1/" +
		accountId +
		"/payroll_time_report_download?levelIdFilter=" +
		levelIdFilter +
		"&referenceDate=" +
		encodeURIComponent(referenceDate) +
		"&asyncSessionId=" +
		asyncSessionId,
		payGroupIds
	)
}

export function getAsyncPayrollTimeReportDataDownload(accountId, levelIdFilter, payGroupIds: string[], referenceDate, asyncSessionId) {
	return axiosInstance.post(
		"/reportingv1/" +
		accountId +
		"/async_payroll_time_report_download?levelIdFilter=" +
		levelIdFilter +
		"&referenceDate=" +
		encodeURIComponent(referenceDate) +
		"&asyncSessionId=" +
		asyncSessionId,
		payGroupIds
	)
}

export function getPayrollTimeDataDownload(accountId, levelIdFilter, payGroupIds: string[], referenceDate, asyncSessionId) {
	return axiosInstance.post(
		"/reportingv1/" +
		accountId +
		"/payroll_time_download?levelIdFilter=" +
		levelIdFilter +
		"&referenceDate=" +
		encodeURIComponent(referenceDate) +
		"&asyncSessionId=" +
		asyncSessionId,
		payGroupIds
	)
}

export function getAsyncPayrollTimeDataDownload(accountId, levelIdFilter, payGroupIds: string[], referenceDate, asyncSessionId) {
	return axiosInstance.post(
		"/reportingv1/" +
		accountId +
		"/async_payroll_time_download?levelIdFilter=" +
		levelIdFilter +
		"&referenceDate=" +
		encodeURIComponent(referenceDate) +
		"&asyncSessionId=" +
		asyncSessionId,
		payGroupIds
	)
}

export function getPayrollExpensesReportDataDownload(accountId, levelIdFilter, payGroupIds: string[], referenceDate) {
	return axiosInstance.post(
		"/reportingv1/" +
		accountId +
		"/payroll_expenses_report_download?levelIdFilter=" +
		levelIdFilter +
		"&referenceDate=" +
		encodeURIComponent(referenceDate),
		payGroupIds
	)
}

export function getPayrollExpensesDataDownload(accountId, levelIdFilter, payGroupIds: string[], referenceDate, priorPeriod: boolean) {
	return axiosInstance.post(
		"/reportingv1/" +
		accountId +
		"/payroll_expenses_download?levelIdFilter=" +
		levelIdFilter +
		"&referenceDate=" +
		encodeURIComponent(referenceDate) +
		"&priorPeriod=" +
		priorPeriod,
		payGroupIds
	)
}

export function checkCanChangeEmployeePayrollPayGroupData(userId, newPayrollPayGroupData: IPayrollPayGroupData) {
	return axiosInstance.post(`users/${userId}/can_change_payrollpaygroupdata`, newPayrollPayGroupData)
}

export function addEmployeePosition(userId, position) {
	return axiosInstance.post(`/users/${userId}/add_position`, position)
}

export function updateEmployeePosition(userId, position) {
	return axiosInstance.post(`/users/${userId}/update_position`, position)
}

export function getEmployeeAccrual(userId) {
	return axiosInstance.post("/users/" + userId + "/employee_accruals")
}

export function addEmployeeAccrual(userId, employeeAccrual) {
	return axiosInstance.post("/users/" + userId + "/employee_accruals", employeeAccrual)
}

export function updateEmployeeAccrual(userId, employeeAccrual) {
	return axiosInstance.put("/users/" + userId + "/employee_accruals", employeeAccrual)
}

export function addManualEmployeeAccrual(userId, employeeAccrual) {
	return axiosInstance.post("/users/" + userId + "/manual_employee_accruals", employeeAccrual)
}

export function updateManualEmployeeAccrual(userId, employeeAccrual) {
	return axiosInstance.put("/users/" + userId + "/manual_employee_accruals", employeeAccrual)
}

export function deleteEmployeeAccrual(userId, employeeAccrualId) {
	return axiosInstance.delete("/users/" + userId + "/employee_accruals/" + employeeAccrualId)
}

export function getEmployeeShifts(userId, startDate, endDate, includePlanShifts) {
	return axiosInstance.get(`/users/${userId}/shifts?startDate=${utils.escapeDateTime(startDate)}&endDate=${utils.escapeDateTime(endDate)}&includePlanShifts=${includePlanShifts}`)
}

export function getLeaveRequest(timeoffId) {
	return axiosInstance.get(`/timeoff/${timeoffId}`)
}

export function addLeaveRequest(leaveRequest, byEmployee: boolean) {
	return axiosInstance.post(`/timeoff?byEmployee=${byEmployee}`, leaveRequest)
}

export function updateLeaveRequest(leaveRequest, byEmployee: boolean) {
	return axiosInstance.put(`/timeoff?byEmployee=${byEmployee}`, leaveRequest)
}

export function updateLeaveRequestNotes(timeoffId: string, timeoffNotes: ITimeoffNote[]) {
	return axiosInstance.put(`/timeoff/${timeoffId}/notes`, timeoffNotes)
}

export function deleteLeaveRequests(timeoffIds, byEmployee: boolean) {
	return axiosInstance.post(`/timeoff/delete?byEmployee=${byEmployee}`, timeoffIds)
}

export function updateLeaveRequestsStatus(timeoffIds, status) {
	return axiosInstance.post(`/timeoff/status?status=${status}`, timeoffIds)
}

export function getEmployeeLeaveRequests(userId, startDate, endDate, page, limit, search, timeoffStatusList) {
	return axiosInstance.post(`/users/${userId}/timeoff?startDate=${startDate}&endDate=${endDate}&page=${page}&limit=${limit}&search=${search}`, timeoffStatusList)
}

export function getSchedules(accountId, startDate, endDate, posted, page, limit, search, levelIdFilter, showAllChildHierarchyData) {
	return axiosInstance.get(`/schedules?accountId=${accountId}&startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}&posted=${posted}&page=${page}
		&limit=${limit}&search=${search}&levelIdFilter=${levelIdFilter}&showAllChildHierarchyData=${showAllChildHierarchyData}`)
}

export function addSchedule(schedule) {
	return axiosInstance.post("/schedules", schedule)
}

export function deleteSchedule(scheduleIds) {
	return axiosInstance.post("/schedules/delete", scheduleIds)
}

export function getSchedule(scheduleId) {
	return axiosInstance.get("/schedules/" + scheduleId)
}

export function updateSchedule(schedule: IScheduleDocument) {
	if (schedule.HiddenSchedules && schedule.HiddenSchedules.length > 0) {
		schedule.Schedules = schedule.Schedules.concat(schedule.HiddenSchedules)
	}
	return axiosInstance.put("/schedules", schedule)
}

export function checkValidationAndUpdateSchedule(schedule: IScheduleDocument, checkValidationShiftIds: string[]) {
	if (schedule.HiddenSchedules && schedule.HiddenSchedules.length > 0) {
		schedule.Schedules = schedule.Schedules.concat(schedule.HiddenSchedules)
	}

	return axiosInstance.put("/schedules/check_validation_update", { schedule, checkValidationShiftIds })
}

export function postSchedulePlanner(scheduleId: string, ignorePendingTimeoffWarningConflict: boolean = false) {
	return axiosInstance.post(`schedules/post_schedule?scheduleId=${scheduleId}&ignorePendingTimeoffWarningConflict=${ignorePendingTimeoffWarningConflict}`)
}

export function getEmployeeSchedules(userId, startDate, endDate) {
	return axiosInstance.get(`/users/${userId}/schedules?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`)
}

export function getAllowChangeEmpAccrual(userId, empAccrualId) {
	return axiosInstance.get(`/users/${userId}/allowchangeempaccrual?empAccrualId=${empAccrualId}`)
}

export function getEmployeeScheduleShiftConstraints(userId, scheduledPatternTemplates, isMasterSchedule) {
	return axiosInstance.post(`/schedules/shifts/${userId}/schedule_constraints?isMasterSchedule=${isMasterSchedule}`, scheduledPatternTemplates)
}

export function autoAssignSchedulePlanner(scheduleId, filterByDirectReports = false, limitToShiftHierarchy = false) {
	return axiosInstance.post(`/schedules/${scheduleId}/auto_assign?filterByDirectReports=${filterByDirectReports}&limitToShiftHierarchy=${limitToShiftHierarchy}`)
}

export function getValidationForNewSchedule(schedule: IScheduleDocument) {
	return axiosInstance.post(`/schedules/validataion_new_schedule`, schedule)
}

export function getValidationForCopySchedule(startDate, schedules) {
	return axiosInstance.post(`/schedules/validataion_copy_schedule?startDate=${encodeURIComponent(startDate)}`, schedules)
}

export function copySchedule(startDate, schedules, publish: boolean, ignorePendingTimeoffWarningConflict: boolean = false) {
	return axiosInstance.post(`/schedules/copy_schedule?startDate=${encodeURIComponent(startDate)}&publish=${publish}&ignorePendingTimeoffWarningConflict=${ignorePendingTimeoffWarningConflict}`, schedules)
}

export function getNotifications(userId) {
	return axiosInstance.get(`/users/${userId}/notifications?isApp=true`)
}

export function clearNotifications(userId) {
	return axiosInstance.delete(`/users/${userId}/notifications?isApp=true`)
}

export function GetAllHolidaysForEmployee(userId) {
	return axiosInstance.get(`/users/${userId}/holidays`)
}

export function DeleteShift(scheduleId, shiftId) {
	return axiosInstance.delete(`/schedules/${scheduleId}/delete_shift/${shiftId}`)
}

export function UpdateShift(scheduleId, shift) {
	return axiosInstance.put(`/schedules/${scheduleId}/update_shift`, shift)
}

export function logout() {
	return axiosInstance.post(`/users/logout`)
}

export function getJob(jobId) {
	return axiosInstance.get(`/jobs/${jobId}`)
}

export function getJobsByIds(jobIds: string[]) {
	return axiosInstance.post("/jobs/get_list", jobIds)
}

export function getJobInfo(jobId) {
	return axiosInstance.get(`/jobs/${jobId}/jobinfo`)
}

export function getNoteTypes(accountId) {
	return axiosInstance.get(`/accounthrissettings/${accountId}/get_note_types`)
}

export function getJobStatuses(accountId) {
	return axiosInstance.get(`/accounthrissettings/${accountId}/get_job_statuses`)
}

export function getUnreadNotificationCount(userId) {
	return axiosInstance.get(`/users/${userId}/notifications/unread_notification_count?isApp=true`)
}

export function setReadNotification(notificationId) {
	return axiosInstance.put(`/notifications/${notificationId}/read`)
}

export function updateJob(job) {
	return axiosInstance.put("/jobs", job)
}

export function deleteJobs(jobIds: string[]) {
	return axiosInstance.post("/jobs/delete", jobIds)
}

export function searchUserJobs(userId: string, searchParams: {
	Page?: number,
	Limit?: number,
	Search?: string,
	BadgeIds?: string[],
	OccupationIds?: string[],
	SpecialtyIds?: string[],
	JobStatusId?: string,
	JobSubStatusId?: string,
	StartDate?: string,
	EndDate?: string,
	ClientId?: string,
	Requested?: boolean,
	Available?: boolean,
	Confirmed?: boolean,
	Miles?: number,
	AccountId?: string
}) {
	return axiosInstance.post(`/users/${userId}/candidate_job_search`, searchParams)
}

export function searchUserJobsResult(userId: string, searchParams: {
	Page?: number,
	Limit?: number,
	Search?: string,
	BadgeIds?: string[],
	OccupationIds?: string[],
	SpecialtyIds?: string[],
	JobStatusId?: string,
	JobSubStatusId?: string,
	StartDate?: string,
	EndDate?: string,
	ClientId?: string,
	Requested?: boolean,
	Available?: boolean,
	Confirmed?: boolean,
	Miles?: number,
	AccountId?: string
}) {
	return axiosInstance.post(`/users/${userId}/candidate_job_search_result`, searchParams)
}

export function addAccountAgreement(agreement) {
	return axiosInstance.post(`/privacyagreement`, agreement)
}

export function updateAccountAgreement(agreement) {
	return axiosInstance.put("/privacyagreement", agreement)
}

export function getLatestPrivacy(accountId = "") {
	return axiosInstance.get(`/privacyagreement/latest?accountId=${accountId}`)
}

export function checkValidateUpdateTimeoff(timeoffId) {
	return axiosInstance.get(`/timeoff/${timeoffId}/validate_update`)
}

export function uploadUserProfilePic(userId, imageData) {
	return axiosInstance.post(`/users/${userId}/upload_profile_picture`, { data: imageData })
}

export function getInOutReportDownload(accountId, levelIdFilter, payGroupIds: string[], userFilter: IUserFilter, referenceDate, filterByDirectReport, filterByDefaultHierarchy, filterByPunchHierarchy, filterByException, filterExceptions: EExceptionType[], asyncSessionId) {
	return axiosInstance.post(
		"/reportingv1/" +
		accountId +
		"/in_out_report_v1?levelIdFilter=" +
		levelIdFilter +
		"&referenceDate=" +
		encodeURIComponent(referenceDate) +
		"&filterByDirectReport=" +
		filterByDirectReport +
		"&filterByDefaultHierarchy=" +
		filterByDefaultHierarchy +
		"&filterByPunchHierarchy=" +
		filterByPunchHierarchy +
		"&filterByException=" +
		filterByException +
		"&asyncSessionId=" +
		asyncSessionId,
		{ payGroupIds, filterExceptions, userFilter }
	)
}

export function getEstimatedTimeReportDownload(accountId, levelIdFilter, payGroupIds: string[], userFilter: IUserFilter, referenceDate, filterByDirectReport, filterByDefaultHierarchy, filterByPunchHierarchy, filterByException, filterExceptions: EExceptionType[], reportType, asyncSessionId) {
	return axiosInstance.post(
		"/reportingv1/" +
		accountId +
		"/estimated_time_report_v1?levelIdFilter=" +
		levelIdFilter +
		"&referenceDate=" +
		encodeURIComponent(referenceDate) +
		"&filterByDirectReport=" +
		filterByDirectReport +
		"&filterByDefaultHierarchy=" +
		filterByDefaultHierarchy +
		"&filterByPunchHierarchy=" +
		filterByPunchHierarchy +
		"&filterByException=" +
		filterByException +
		"&reportType=" +
		reportType +
		"&asyncSessionId=" +
		asyncSessionId,
		{ payGroupIds, filterExceptions, userFilter }
	)
}

export function getHierarchyPunchReportDownload(accountId, hierarchyId, startDate, endDate, restrictDate, asyncSessionId) {
	return axiosInstance.get(
		"/reporting/" +
		accountId +
		"/hierarchy_punch_report?hierarchyId=" +
		hierarchyId +
		"&startDate=" +
		encodeURIComponent(startDate) +
		"&endDate=" +
		endDate +
		"&restrictDate=" +
		restrictDate +
		"&asyncSessionId=" +
		asyncSessionId
	)
}

export function getTimecardAuditReportDownload(accountId, levelIdFilter, payGroupIds: string[], userFilter: IUserFilter, referenceDate, filterByDefaultHierarchy, filterByDirectReport, filterByPunchHierarchy, filterByException, filterExceptions: EExceptionType[], includeAllAudits, asyncSessionId, endDate) {
	return axiosInstance.post(
		"/reportingv1/" +
		accountId +
		"/timecard_audit_report_v1?levelIdFilter=" +
		levelIdFilter +
		"&referenceDate=" +
		encodeURIComponent(referenceDate) +
		"&endDate=" +
		encodeURIComponent(endDate) +
		"&filterByDefaultHierarchy=" +
		filterByDefaultHierarchy +
		"&filterByDirectReport=" +
		filterByDirectReport +
		"&filterByPunchHierarchy=" +
		filterByPunchHierarchy +
		"&filterByException=" +
		filterByException +
		"&includeAllAudits=" +
		includeAllAudits +
		"&asyncSessionId=" +
		asyncSessionId,
		{ payGroupIds, filterExceptions, userFilter }
	)
}

export function addJob(job) {
	return axiosInstance.post("/jobs", job)
}

export function checkValidateJob(job) {
	return axiosInstance.post("/jobs/check_job_validation", job)
}

export function checkJobHierarchyMoveValidatation(job: IJobDocument) {
	return axiosInstance.post("/jobs/check_hierarchy_move_validation", job)
}

export function getMasterSchedules(accountId, page, limit, search, levelIdFilter, showAllChildHierarchyData, bySupervisorPermission = false) {
	return axiosInstance.get(`/schedules/master_schedules?accountId=${accountId}&page=${page}&limit=${limit}&search=${search}
	&levelIdFilter=${levelIdFilter}&showAllChildHierarchyData=${showAllChildHierarchyData}&bySupervisorPermission=${bySupervisorPermission}`)
}

export function getJobMasterSchedules(page, limit, search, jobId) {
	return axiosInstance.get(`/jobs/master_schedules?page=${page}&limit=${limit}&search=${search}&jobId=${jobId}`)
}

export function getAccountHRISOccupations(accountId) {
	return axiosInstance.get(`/accounthrissettings/${accountId}/get_occupations`)
}

export function getAccountHRISSpecialties(accountId) {
	return axiosInstance.get(`/accounthrissettings/${accountId}/get_specialities`)
}

export function getAccountHRISBadges(accountId) {
	return axiosInstance.get(`/accounthrissettings/${accountId}/get_badges`)
}

export function getScheduleReport(scheduleId) {
	return axiosInstance.get(`/reporting/schedule_report/${scheduleId}`)
}

export function getAdhocReport(accountId: string, levelIdFilter: string, filterByDefaultHierarchy: boolean, filterByDirectReport: boolean, filterByPunchHierarchy: boolean, filterByException: boolean, filterExceptions: EExceptionType[], request: ITimecardAdhocExportRequest, userFilter: IUserFilter) {
	return axiosInstance.post(
		"/reportingv1/" +
		accountId +
		"/adhoc_report_v1?levelIdFilter=" +
		levelIdFilter +
		"&filterByDefaultHierarchy=" +
		filterByDefaultHierarchy +
		"&filterByDirectReport=" +
		filterByDirectReport +
		"&filterByPunchHierarchy=" +
		filterByPunchHierarchy +
		"&filterByException=" +
		filterByException,
		{ request, filterExceptions, userFilter }
	)
}

export function addAdhocReport(accountId: string, request: ITimecardAdhocExportRequest): Promise<AxiosResponse<IUserReportDocument>> {
	return axiosInstance.post(`/users/${accountId}/reports/add_timecard_adhoc_report`, request)
}

export function fetchAdhocReports(accountId: string): Promise<AxiosResponse<IUserReportDocument>> {
	return axiosInstance.get(`/users/${accountId}/reports`)
}

export function deleteAdhocReport(accountId: string, reportId: string): Promise<AxiosResponse<IUserReportDocument>> {
	return axiosInstance.delete(`/users/${accountId}/reports/delete_timecard_adhoc_report/${reportId}`)
}

export function updateAdhocReport(accountId: string, request: ITimecardAdhocExportRequest): Promise<AxiosResponse<IUserReportDocument>> {
	return axiosInstance.put(`/users/${accountId}/reports/update_timecard_adhoc_report`, request)
}

export function getPunchDetailReport(accountId: string, startDate: string, endDate: string, levelIdFilter: string, payGroupIds: string[], userFilter: IUserFilter, filterByDefaultHierarchy: boolean, filterByDirectReport: boolean, filterByPunchHierarchy: boolean, filterByException: boolean, filterExceptions: EExceptionType[]) {
	return axiosInstance.post(
		"/reportingv1/" +
		accountId +
		"/punch_detail_report_v1?levelIdFilter=" +
		levelIdFilter +
		"&startDate=" +
		encodeURIComponent(startDate) +
		"&endDate=" +
		encodeURIComponent(endDate) +
		"&filterByDefaultHierarchy=" +
		filterByDefaultHierarchy +
		"&filterByDirectReport=" +
		filterByDirectReport +
		"&filterByPunchHierarchy=" +
		filterByPunchHierarchy +
		"&filterByException=" +
		filterByException,
		{ payGroupIds, filterExceptions, userFilter }
	)
}

export function getManualEntriesReport(accountId: string, startDate: string, endDate: string, levelIdFilter: string, payGroupIds: string[], userFilter: IUserFilter, filterByDefaultHierarchy: boolean, filterByDirectReport: boolean, filterByPunchHierarchy: boolean, filterByException: boolean, filterExceptions: EExceptionType[]) {
	return axiosInstance.post(
		"/reportingv1/" +
		accountId +
		"/manual_entries_report_v1?levelIdFilter=" +
		levelIdFilter +
		"&startDate=" +
		encodeURIComponent(startDate) +
		"&endDate=" +
		encodeURIComponent(endDate) +
		"&filterByDefaultHierarchy=" +
		filterByDefaultHierarchy +
		"&filterByDirectReport=" +
		filterByDirectReport +
		"&filterByPunchHierarchy=" +
		filterByPunchHierarchy +
		"&filterByException=" +
		filterByException,
		{ payGroupIds, filterExceptions, userFilter }
	)
}

export function getPunchDetailReportWithPhoto(accountId: string, startDate: string, endDate: string, levelIdFilter: string, payGroupIds: string[], userFilter: IUserFilter, filterByDefaultHierarchy: boolean, filterByDirectReport: boolean, filterByPunchHierarchy: boolean, filterByException: boolean, filterExceptions: EExceptionType[], asyncSessionId: string) {
	return axiosInstance.post(
		"/reportingv1/" +
		accountId +
		"/punch_detail_report_v1_with_photo?levelIdFilter=" +
		levelIdFilter +
		"&startDate=" +
		encodeURIComponent(startDate) +
		"&endDate=" +
		encodeURIComponent(endDate) +
		"&filterByDefaultHierarchy=" +
		filterByDefaultHierarchy +
		"&filterByDirectReport=" +
		filterByDirectReport +
		"&filterByPunchHierarchy=" +
		filterByPunchHierarchy +
		"&filterByException=" +
		filterByException +
		"&asyncSessionId=" +
		asyncSessionId,
		{ payGroupIds, filterExceptions, userFilter }
	)
}

export function getPunchQuestionResponseReport(accountId: string, startDate: string, endDate: string, levelIdFilter: string, payGroupIds: string[], userFilter: IUserFilter, filterByDefaultHierarchy: boolean, filterByDirectReport: boolean, filterByPunchHierarchy: boolean, filterByException: boolean, filterExceptions: EExceptionType[]) {
	return axiosInstance.post(
		"/reportingv1/" +
		accountId +
		"/punch_question_response_report_v1?levelIdFilter=" +
		levelIdFilter +
		"&startDate=" +
		encodeURIComponent(startDate) +
		"&endDate=" +
		encodeURIComponent(endDate) +
		"&filterByDefaultHierarchy=" +
		filterByDefaultHierarchy +
		"&filterByDirectReport=" +
		filterByDirectReport +
		"&filterByPunchHierarchy=" +
		filterByPunchHierarchy +
		"&filterByException=" +
		filterByException,
		{ payGroupIds, filterExceptions, userFilter }
	)
}

export function getTimecardSummaryReport(accountId: string, referenceDate: string, levelIdFilter: string, payGroupIds: string[], userFilter: IUserFilter, filterByDefaultHierarchy: boolean, filterByDirectReport: boolean, filterByPunchHierarchy: boolean, filterByException: boolean, filterExceptions: EExceptionType[]) {
	return axiosInstance.post(`/reportingv1/${accountId}/time_card_summary_report_v1?
		referenceDate=${utils.escapeDateTime(referenceDate)}&
		levelIdFilter=${levelIdFilter}&
		filterByDefaultHierarchy=${filterByDefaultHierarchy}&
		filterByDirectReport=${filterByDirectReport}&
		filterByPunchHierarchy=${filterByPunchHierarchy}&
		filterByException=${filterByException}`,
		{ payGroupIds, filterExceptions, userFilter }
	)
}

/**
 * Retrieves biometric & location exceptions report data via POST.
 * 
 * @param accountId - The account Id.
 * @param startDate - Start date/time (string in ISO format).
 * @param endDate - End date/time (string in ISO format).
 * @param levelIdFilter - Hierarchy level ID filter.
 * @param payGroupIds - Array of pay group IDs.
 * @param filterByDefaultHierarchy - Whether to filter by default hierarchy.
 * @param filterByDirectReport - Whether to filter by direct reports only.
 * @param filterByPunchHierarchy - Whether to filter by punch hierarchy.
 * @param filterByException - Whether to filter by exceptions.
 * @returns Promise resolving the raw data of the report.
 */
export function getBiometricLocationExceptionsReport(
	accountId: string,
	startDate: string,
	endDate: string,
	levelIdFilter: string,
	payGroupIds: string[],
	userFilter: IUserFilter,
	filterByDefaultHierarchy: boolean,
	filterByDirectReport: boolean,
	filterByPunchHierarchy: boolean,
	filterByException: boolean,
	filterExceptions: EExceptionType[]
) {
	return axiosInstance.post(`/reportingv1/${accountId}/biometric_location_exceptions_report_v1?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}&levelIdFilter=${levelIdFilter}&filterByDefaultHierarchy=${filterByDefaultHierarchy}&filterByDirectReport=${filterByDirectReport}&filterByPunchHierarchy=${filterByPunchHierarchy}&filterByException=${filterByException}`, { payGroupIds, filterExceptions, userFilter })
}

export function getClients(accountId, levelIdFilter, page, limit, search, clientActiveStatus: EClientActiveStatus | "all") {
	return axiosInstance.get(`/clients/${accountId}/all?levelIdFilter=${levelIdFilter}&page=${page}&limit=${limit}&search=${search}&clientActiveStatus=${clientActiveStatus === "all" ? "" : clientActiveStatus}`)
}

export function getClient(clientId) {
	return axiosInstance.get(`/clients/${clientId}`)
}

export function addClient(client) {
	return axiosInstance.post("/clients", client)
}

export function deleteClient(clientIDs) {
	return axiosInstance.delete("/clients/delete_clients", clientIDs)
}

export function getMultiClients(clientIDs: string[]) {
	return axiosInstance.post("/clients/get_list", clientIDs)
}

export function getFile(fileDocumentId) {
	return axiosInstance.get(`/files/${fileDocumentId}`)
}

export function uploadFile(file) {
	return axiosInstance.post("/files/upload", file)
}

export function removeUploadedFile(fileDocumentId) {
	return axiosInstance.delete(`/files/${fileDocumentId}`)
}

export function getEmployeeExpenses(userId: string, referenceDate: Date | string) {
	return axiosInstance.get(`/expenses/employee_expenses?userId=${userId}&referenceDate=${utils.escapeDateTime(referenceDate)}`)
}

export function getEmployeeStatuses(accountId: string) {
	return axiosInstance.get(`/accounthrissettings/${accountId}/get_employee_statuses`)
}

export function addExpense(expenseBundle: IExpenseBundle, byEmployee: boolean) {
	return axiosInstance.post(`/expenses/expense_bundle?byEmployee=${byEmployee}`, expenseBundle)
}

export function updateExpense(expenseBundle: IExpenseBundle, byEmployee: boolean) {
	return axiosInstance.put(`/expenses/expense_bundle?byEmployee=${byEmployee}`, expenseBundle)
}

export function deleteExpenses(expenseIds: string[], byEmployee: boolean) {
	return axiosInstance.post(`/expenses/delete_many?byEmployee=${byEmployee}`, expenseIds)
}

export function updateExpensesApproveStatus(expenseIds: string[], approveType: EApproveType) {
	return axiosInstance.post(`/expenses/update_approve_status?approveType=${approveType}`, expenseIds)
}

export function getPayperiodStatus(accountId, payrollPayGroupData: IPayrollPayGroupData, referenceDate, payrollType: EPayrollType) {
	return axiosInstance.post(`/accounts/${accountId}/payperiod_status_v1?referenceDate=${utils.escapeDateTime(referenceDate)}&payrollType=${payrollType}`, payrollPayGroupData)
}

export function getJobSchedules(jobId, startDate, endDate, includePlanShifts, page, limit, search) {
	return axiosInstance.get(`/jobs/${jobId}/schedules?startDate=${utils.escapeDateTime(startDate)}&endDate=${utils.escapeDateTime(endDate)}&includePlanShifts=${includePlanShifts}&page=${page}&limit=${limit}&search=${search}`)
}

export function addJobShifts(jobId: string, newPatternTemplates: ISchedulePatternTemplate[]) {
	return axiosInstance.post(`/jobs/${jobId}/schedules`, newPatternTemplates)
}

export function getJobExpenses(jobId: string) {
	return axiosInstance.get(`jobs/expense_templates?jobId=${jobId}`)
}

export function addJobExpense(jobId: string, expense: IExpenseTemplate) {
	return axiosInstance.post(`jobs/expense_templates?jobId=${jobId}`, expense)
}

export function deleteJobExpenses(jobId: string, expenseIds: string[]) {
	return axiosInstance.delete(`jobs/expense_templates?jobId=${jobId}`, { data: expenseIds })
}

export function updateJobExpense(jobId: string, expense: IExpenseTemplate) {
	return axiosInstance.put(`jobs/expense_templates?jobId=${jobId}`, expense)
}

export function getJobTimecards(jobId, payGroupIds: string[], referenceDate, page, limit, userFilter: IUserFilter = null) {
	return axiosInstance.post(`/jobs/${jobId}/timecards?referenceDate=${utils.escapeDateTime(referenceDate)}&page=${page}&limit=${limit}`, { payGroupIds, userFilter })
}

export function getJobConsolidatedTimecardDataV2(jobId, startDate, endDate, userFilter: IUserFilter = null) {
	return axiosInstance.post(`/jobs/${jobId}/consolidated_timecard_data?startDate=${utils.escapeDateTime(startDate)}&endDate=${utils.escapeDateTime(endDate)}`, userFilter)
}
export function getJobConsolidatedTimecardData(jobId, startDate, endDate) {
	return axiosInstance.get(`/jobs/${jobId}/consolidated_timecard_data?startDate=${utils.escapeDateTime(startDate)}&endDate=${utils.escapeDateTime(endDate)}`)
}

export function getMultiJobsBudgetData(jobFilter: IJobFilter, startDate, endDate) {
	return axiosInstance.post(`/jobs/jobs_budget_data?startDate=${utils.escapeDateTime(startDate)}&endDate=${utils.escapeDateTime(endDate)}`, jobFilter)
}

export function getWorkPoolEmployees(jobId: string, searchParams: Partial<WorkPoolEmployeeSearchParams>) {
	return axiosInstance.post(`/jobs/${jobId}/get_workpoolemployees`, searchParams)
}

export function updateClient(client) {
	return axiosInstance.put("/clients", client)
}

export function uploadClientLogo(clientId, imageData) {
	return axiosInstance.post(`/clients/${clientId}/upload_logo`, { data: imageData })
}

export function removeClientLogo(clientId) {
	return axiosInstance.post(`/clients/${clientId}/remove_logo`)
}

export function getClientContacts(clientId, activeFilter) {
	return axiosInstance.get(`/clientcontacts/${clientId}/all?active=${activeFilter}`)
}

export function addClientContact(clientContact) {
	return axiosInstance.post("/clientcontacts", clientContact)
}

export function deleteClientContact(clientContactIDs) {
	return axiosInstance.delete("/clientcontacts/delete_contacts", clientContactIDs)
}

export function updateClientContact(clientContact) {
	return axiosInstance.put("/clientcontacts", clientContact)
}

export function uploadClientContactProfilePicture(clientContactId, imageData) {
	return axiosInstance.post(`/clientcontacts/${clientContactId}/upload_profile_image`, { data: imageData })
}

export function removeClientContactProfilePicture(clientContactId) {
	return axiosInstance.post(`/clientcontacts/${clientContactId}/remove_profile_image`)
}

export function checkClientNameValid(clientName) {
	return axiosInstance.get(`/clients/name_valid?clientName=${clientName}`)
}

export function checkClientExternalIdValid(externalId) {
	return axiosInstance.get(`/clients/externalId_valid?externalId=${externalId}`)
}

export function postClientMassJobs(job: IMassJobCreation) {
	return axiosInstance.post(`jobs/create_mass_job_shifts`, job)
}

export function getManyPunches(punchIds: string[]) {
	return axiosInstance.post("/punches/get_many_punches", punchIds)
}

export function getManyUsers(userIds: string[]) {
	return axiosInstance.post("/users/get_many_users", userIds)
}
export function getNotes(notesPayload) {
	return axiosInstance.post("/notes/search", notesPayload)
}

export function addNote(notePayload) {
	return axiosInstance.post("/notes", notePayload)
}

export function getClientPositions(clientId) {
	return axiosInstance.get(`/clients/position?clientId=${clientId}`)
}

export function addClientPosition(clientId: string, position: IPosition) {
	return axiosInstance.post(`/clients/position?clientId=${clientId}`, position)
}

export function updateClientPosition(clientId: string, position: IPosition) {
	return axiosInstance.put(`/clients/position?clientId=${clientId}`, position)
}

export function deleteClientPositions(clientId: string, positionIds: string[]) {
	return axiosInstance.delete(`/clients/position?clientId=${clientId}`, { data: positionIds })
}

export function getJobPositions(jobId) {
	return axiosInstance.get(`/jobs/position?jobId=${jobId}`)
}

export function getClientSchedules(clientId, startDate, endDate, includePlanShifts, page, limit, search) {
	return axiosInstance.get(`/clients/${clientId}/schedules?startDate=${utils.escapeDateTime(startDate)}&endDate=${utils.escapeDateTime(endDate)}&includePlanShifts=${includePlanShifts}&page=${page}&limit=${limit}&search=${search}`)
}

export function getClientShiftsReport(clientId, startDate, endDate) {
	return axiosInstance.get(`/reporting/client_shifts_report?clientId=${clientId}&startDate=${utils.escapeDateTime(startDate)}&endDate=${utils.escapeDateTime(endDate)}`)
}

export function addJobPosition(jobId: string, position: IPosition) {
	return axiosInstance.post(`/jobs/position?jobId=${jobId}`, position)
}

export function updateJobPosition(jobId: string, position: IPosition) {
	return axiosInstance.put(`/jobs/position?jobId=${jobId}`, position)
}

export function deleteJobPositions(jobId: string, positionIds: string[]) {
	return axiosInstance.delete(`/jobs/position?jobId=${jobId}`, { data: positionIds })
}

export function getShift(shiftId: string) {
	return axiosInstance.get(`/schedules/shift/${shiftId}`)
}

export function getWorkPoolEmployeesByShiftId(shiftId: string, searchParams: Partial<WorkPoolEmployeeSearchParams>) {
	return axiosInstance.post(`/schedules/${shiftId}/get_workpoolemployees`, searchParams)
}

export function shortListWorkPoolEmployee(jobId, employeeId) {
	return axiosInstance.get(`/jobs/${jobId}/shortlist_candidate?employeeId=${employeeId}`)
}

export function archiveWorkPoolEmployee(jobId, employeeId) {
	return axiosInstance.get(`/jobs/${jobId}/archive_candidate?employeeId=${employeeId}`)
}

export function archiveWorkPoolShiftEmployee(shiftId, employeeId) {
	return axiosInstance.get(`/schedules/${shiftId}/archive_candidate?employeeId=${employeeId}`)
}

export function shortListSelectedCandidates(jobId: string, employeeIds) {
	return axiosInstance.post(`/jobs/${jobId}/shortlist_selected_candidates`, employeeIds)
}

export function shortListSelectedShiftCandidates(shiftId: string, employeeIds) {
	return axiosInstance.post(`/schedules/${shiftId}/shortlist_selected_candidates`, employeeIds)
}

export function createAccountHRISSettingsFromAccount() {
	return axiosInstance.get("/accounthrissettings/create_from_account")
}

export function getEmployeeHR(userId: string) {
	return axiosInstance.get(`/users/${userId}/HR`)
}
export function getJobsByFilter(jobSearchParams: IJobSearchParams) {
	return axiosInstance.post("/jobs/search", jobSearchParams)
}

export function getShiftNote(shiftId: string) {
	return axiosInstance.get(`/schedules/shift/${shiftId}/note`)
}

export function getRequestedJobsByUser(userId: string) {
	return axiosInstance.get(`/users/${userId}/jobs/requested`)
}

export function requestJobForEmployee(jobId: string, userId: string) {
	return axiosInstance.post(`/users/${userId}/jobs/requested?jobId=${jobId}`)
}

export function getSchedulePreference(userId: string) {
	return axiosInstance.get(`/users/${userId}/schedule_preferences`)
}

export function addSchedulePreference(schedulePreference: ISchedulePreference, userId: string) {
	return axiosInstance.post(`/users/${userId}/schedule_preferences`, schedulePreference)
}

export function updateSchedulePreference(schedulePreference: ISchedulePreference, userId: string) {
	return axiosInstance.put(`/users/${userId}/schedule_preferences/${schedulePreference.Id}`, schedulePreference)
}

export function deleteSchedulePreference(userId: string, schedulePreferenceId: string) {
	return axiosInstance.delete(`/users/${userId}/schedule_preferences/${schedulePreferenceId}`)
}

export function deleteSchedulePreferences(userId: string, schedulePreferenceIds: string[]) {
	return axiosInstance.delete(`/users/${userId}/schedule_preferences`, { data: schedulePreferenceIds })
}

export function assignShift(employeeId: string, shiftIds: string[]) {
	return axiosInstance.post(`/schedules/assign_shift?employeeId=${employeeId}`, shiftIds)
}

export function checkUserDeleted(userId: string) {
	return axiosInstance.get(`/users/${userId}/check_deleted`)
}

export function recalculateEmployeeTimecard(userId: string, referenceDate: string) {
	return axiosInstance.get(`/users/${userId}/recalc_timecard?referenceDate=${utils.escapeDateTime(referenceDate)}`)
}

export function updatePunchRuleProperty(userId: string, punchRuleProperty: IRuleProperty) {
	return axiosInstance.put(`/users/${userId}/punchrule_property`, punchRuleProperty)
}

export function addQuestion(questionDoc: IQuestionDocument) {
	return axiosInstance.post("/questions", questionDoc)
}

export function getQuestion(questionDocId: string) {
	return axiosInstance.get(`/questions/${questionDocId}`)
}

export function getQuestionByQuestionGUID(questionGUId: string) {
	return axiosInstance.get(`/questions/by_quest_guid/${questionGUId}`)
}

export function getQuestionsByQuestionGUIDs(questionGUIds: string[]) {
	return axiosInstance.post(`/questions/by_quest_guids`, questionGUIds)
}

export function addClientType(accountId, clientType) {
	return axiosInstance.post(`/accounthrissettings/${accountId}/add_client_type`, clientType)
}

export function updateClientType(accountId, clientType) {
	return axiosInstance.put(`/accounthrissettings/${accountId}/update_client_type`, clientType)
}

export function deleteClientType(accountId, clientTypeId) {
	return axiosInstance.delete(`/accounthrissettings/${accountId}/delete_client_type?id=${clientTypeId}`)
}

export function getClientTypes(accountId) {
	return axiosInstance.get(`/accounthrissettings/${accountId}/get_client_types`)
}

export function getUIUser(userId) {
	return axiosInstance.get(`/users/${userId}/ui`)
}

export function getBasicUsers(userIds: string[]) {
	return axiosInstance.post(`/users/basic_users`, userIds)
}

export function deleteEmployees(employeeIds: string[]) {
	return axiosInstance.delete("/users/delete_employees", { data: employeeIds })
}

export function saveMassEditJobData(massEditData: IMassJobEdit): Promise<string[]> {
	return axiosInstance.post('/jobs/edit_mass_jobs', massEditData)
}

export function addAssignedEmployee(jobId: string, employeeId: string) {
	return axiosInstance.post(`/jobs/${jobId}/assign_employee?employeeId=${employeeId}`)
}

export function addAssignedEmployees(jobId: string, employeeIds: string[]) {
	return axiosInstance.post(`/jobs/${jobId}/assign_employees`, employeeIds)
}

export function unAssignEmployees(jobId: string, userIds: string[]) {
	return axiosInstance.post(`/jobs/${jobId}/unassign_employees`, userIds)
}
export function singleDaySDRule(file) {
	return axiosInstance.post(`/punchrulescript/SingleDaySDRule`, file)
}

export function multipleDaySDRule(file) {
	return axiosInstance.post(`/punchrulescript/MultipleDaySDRule`, file)
}

export function punchDayGroupingRule() {
	return axiosInstance.get(`/punchrulescript/PunchDayGroupingRule`)
}

export function dailyPunchRule() {
	return axiosInstance.get(`/punchrulescript/DailyPunchRule`)
}

export function GuaranteedMinimumHourPunchRule(file) {
	return axiosInstance.post(`/punchrulescript/GuaranteedMinimumHourPunchRule`, file)
}

export function MealPenaltyWaiverPunchRule(file) {
	return axiosInstance.post(`/punchrulescript/MealPenaltyWaiverPunchRule`, file)
}

export function BonusPayPunchRule(file) {
	return axiosInstance.post(`/punchrulescript/BonusPayPunchRule`, file)
}

export function getScheduledJobs(accountId: string, showSystemJobs: boolean, showAllJobs: boolean, showClientOnlyJobs: boolean) {
	return axiosInstance.get(`/accounts/${accountId}/scheduled_jobs?showSystemJobs=${showSystemJobs}&showAllJobs=${showAllJobs}&showClientOnlyJobs=${showClientOnlyJobs}`)
}

export function addScheduledJob(accountId: string, scheduledJob: IScheduledJob) {
	return axiosInstance.post(`/accounts/${accountId}/scheduled_jobs`, scheduledJob)
}

export function updateScheduledJob(accountId: string, scheduledJob: IScheduledJob) {
	return axiosInstance.put(`/accounts/${accountId}/scheduled_jobs`, scheduledJob)
}

export function deleteScheduledJob(accountId: string, scheduledJobId: string) {
	return axiosInstance.delete(`/accounts/${accountId}/scheduled_jobs/${scheduledJobId}`)
}

export function getPayrollExportScheduledJob(accountId: string) {
	return axiosInstance.get(`/accounts/${accountId}/payroll_export_scheduled_job`)
}

export function compileCheckScheduledJob(accountId: string, scheduledJob: IScheduledJob) {
	return axiosInstance.post(`/accounts/${accountId}/scheduled_jobs/compile`, scheduledJob)
}

export function qaCompileCheckScheduledJob(accountId: string, scheduledJob: IScheduledJob) {
	return axiosInstance.post(`/accounts/${accountId}/scheduled_jobs/qacompile`, scheduledJob)
}

export function getLatestScriptLog(accountId: string, scheduledJobId: string) {
	return axiosInstance.get(`/accounts/${accountId}/scheduled_jobs/${scheduledJobId}/script_log`)
}

export function runScript(accountId: string, scheduledJobId: string) {
	return axiosInstance.get(`/accounts/${accountId}/scheduled_jobs/${scheduledJobId}/run`)
}

export function runPayrollExportScript(accountId: string, scheduledJobId: string) {
	return axiosInstance.get(`/accounts/${accountId}/payroll_export_scheduled_jobs/${scheduledJobId}/run`)
}

export function deletePayrollExportScheduleJobRequest(accountId: string) {
	return axiosInstance.delete(`/accounts/${accountId}/payroll_export_scheduled_job_request`)
}

export function deleteScheduleJobRequest(accountId: string, scheduledJobId: string) {
	return axiosInstance.delete(`/accounts/${accountId}/scheduled_jobs/${scheduledJobId}/request`)
}

export function importPunchData(csvFileData: string[][], asyncSessionId: string) {
	return axiosInstance.post(`import/punch?asyncSessionId=${asyncSessionId}`, csvFileData)
}

export function importExpenseTimeData(csvFileData: string[][], asyncSessionId: string) {
	return axiosInstance.post(`import/expense_time?asyncSessionId=${asyncSessionId}`, csvFileData)
}

export function importExpenseAmountData(csvFileData: string[][], asyncSessionId: string) {
	return axiosInstance.post(`import/expense_amount?asyncSessionId=${asyncSessionId}`, csvFileData)
}

export function importEmployeesData(csvFile) {
	return axiosInstance.post("import/employees", csvFile)
}

export function importJobData(csvFile) {
	return axiosInstance.post("import/jobs", csvFile)
}

export function importScheduleData(csvFile, importAsPostedSchedule: boolean) {
	return axiosInstance.post(`import/schedule?importAsPostedSchedule=${importAsPostedSchedule}`, csvFile)
}

export function forceMobileSync(userId: string) {
	return axiosInstance.get(`users/${userId}/force_mobile_sync`)
}

export function getJobTimecardAdhocReport(accountId: string, jobId: string, request: ITimecardAdhocExportRequest, userFilter: IUserFilter) {
	return axiosInstance.post(`/reportingv1/${accountId}/job_timecard_adhoc_report_v1?jobId=${jobId}`, { request, userFilter })
}

export function getJobTimecardPunchDetailReport(accountId: string, jobId: string, startDate: string, endDate: string, payGroupIds: string[], userFilter: IUserFilter, includePunchPhoto: boolean, asyncSessionId: string) {
	return axiosInstance.post(`/reportingv1/${accountId}/job_timecard_punch_detail_report_v1?
		jobId=${jobId}&
		startDate=${encodeURIComponent(startDate)}&
		endDate=${encodeURIComponent(endDate)}&
		includePunchPhoto=${includePunchPhoto}&
		asyncSessionId=${asyncSessionId}`,
		{ payGroupIds, userFilter }
	)
}

export function getJobTimecardManualEntriesReport(accountId: string, jobId: string, startDate: string, endDate: string, payGroupIds: string[], userFilter: IUserFilter) {
	return axiosInstance.post(`/reportingv1/${accountId}/job_timecard_manual_entries_report_v1?
		jobId=${jobId}&
		startDate=${encodeURIComponent(startDate)}&
		endDate=${encodeURIComponent(endDate)}`,
		{ payGroupIds, userFilter }
	)
}

export function getJobTimecardPunchQuestionResponseReport(accountId: string, jobId: string, startDate: string, endDate: string, payGroupIds: string[], userFilter: IUserFilter) {
	return axiosInstance.post(`/reportingv1/${accountId}/job_timecard_punch_question_response_report_v1?
		jobId=${jobId}&
		startDate=${encodeURIComponent(startDate)}&
		endDate=${encodeURIComponent(endDate)}`,
		{ payGroupIds, userFilter }
	)
}

export function getJobTimecardSummaryReport(accountId: string, jobId: string, referenceDate: string, payGroupIds: string[], userFilter: IUserFilter) {
	return axiosInstance.post(`/reportingv1/${accountId}/job_timecard_summary_report_v1?
		jobId=${jobId}&
		referenceDate=${utils.escapeDateTime(referenceDate)}`,
		{ payGroupIds, userFilter }
	)
}

export function getJobTimecardAuditReportDownload(accountId, jobId, payGroupIds: string[], userFilter: IUserFilter, referenceDate, includeAllAudits, asyncSessionId, endDate) {
	return axiosInstance.post(`/reportingv1/${accountId}/job_timecard_audit_report_v1?
		jobId=${jobId}&
		referenceDate=${utils.escapeDateTime(referenceDate)}&
		includeAllAudits=${includeAllAudits}&
		asyncSessionId=${asyncSessionId}&endDate=${endDate}`,
		{ payGroupIds, userFilter }
	)
}

export function getDashboardMapChartData(hierarchyItemId, utcTime, filterDirectReportsOnly, filterByPunchPermissionHierarchy) {
	return axiosInstance.get(`/dashboard/map_chart_data?
		hierarchyItemId=${hierarchyItemId}&
		utcTime=${utils.escapeDateTime(utcTime)}&
		filterDirectReportsOnly=${filterDirectReportsOnly}&
		filterByPunchPermissionHierarchy=${filterByPunchPermissionHierarchy}`,
		{ withCredentials: true }
	)
}

export function getWorkforceAssessmentDashboard(hierarchyId, startDate, endDate) {
	return axiosInstance.get(`/dashboard/workforce_assessment?		
		startDate=${utils.escapeDateTime(startDate)}&
		endDate=${utils.escapeDateTime(endDate)}&hierarchyId=${hierarchyId}`
	)
}

export function getDashboardWorkingEmployees(hierarchyItemId, utcTime, filterDirectReportsOnly, filterByPunchPermissionHierarchy, offset, limit) {
	return axiosInstance.get(`/dashboard/working_employees?
		hierarchyItemId=${hierarchyItemId}&
		utcTime=${utils.escapeDateTime(utcTime)}&
		filterDirectReportsOnly=${filterDirectReportsOnly}&
		filterByPunchPermissionHierarchy=${filterByPunchPermissionHierarchy}&
		page=${offset / limit}&
		limit=${limit}`
	)
}

export function getDashboardWorkingEmployeesDownloadReport(hierarchyItemId, utcTime, filterDirectReportsOnly, filterByPunchPermissionHierarchy) {
	return axiosInstance.get(`/dashboard/working_employees_download_report?
		hierarchyItemId=${hierarchyItemId}&
		utcTime=${utils.escapeDateTime(utcTime)}&
		filterDirectReportsOnly=${filterDirectReportsOnly}&
		filterByPunchPermissionHierarchy=${filterByPunchPermissionHierarchy}`
	)
}

export function getDashboardWorkingEmployeesAttendanceEmergencyReport(hierarchyItemId, utcTime, filterDirectReportsOnly, filterByPunchPermissionHierarchy) {
	return axiosInstance.get(`/dashboard/working_employees_attendance__emergency_report?
		hierarchyItemId=${hierarchyItemId}&
		utcTime=${utils.escapeDateTime(utcTime)}&
		filterDirectReportsOnly=${filterDirectReportsOnly}&
		filterByPunchPermissionHierarchy=${filterByPunchPermissionHierarchy}`
	)
}

export function getDashboardOTEmployees(type: EOTEmployeeType, hierarchyItemId, utcTime, filterDirectReportsOnly, filterByPunchPermissionHierarchy, offset, limit) {
	return axiosInstance.get(`/dashboard/ot_employees?
		hierarchyItemId=${hierarchyItemId}&
		utcTime=${utils.escapeDateTime(utcTime)}&
		filterDirectReportsOnly=${filterDirectReportsOnly}&
		filterByPunchPermissionHierarchy=${filterByPunchPermissionHierarchy}&
		type=${type}&
		page=${offset / limit}&
		limit=${limit}`
	)
}

export function getDashboardOTEmployeesDownloadReport(type: EOTEmployeeType, hierarchyItemId, utcTime, filterDirectReportsOnly, filterByPunchPermissionHierarchy) {
	return axiosInstance.get(`/dashboard/ot_employees_download_report?
		hierarchyItemId=${hierarchyItemId}&
		utcTime=${utils.escapeDateTime(utcTime)}&
		filterDirectReportsOnly=${filterDirectReportsOnly}&
		filterByPunchPermissionHierarchy=${filterByPunchPermissionHierarchy}&
		type=${type}`
	)
}

export function deleteRejectSuspensePunches(punchIds: string[]) {
	return axiosInstance.post("/punches/delete_reject_suspense_punches", punchIds)
}

export function acceptSuspensePunches(punchIds: string[]) {
	return axiosInstance.post("/punches/accept_suspense_punches", punchIds)
}

export function getWorkHistoryPunches({ userId, referenceDateInUtc }: { userId: string, referenceDateInUtc: string }): Promise<AxiosResponse<IWorkHistoryDay>> {
	return axiosInstance.get(`/punches/work_history_day?userId=${userId}&referenceDateInUtc=${encodeURIComponent(referenceDateInUtc)}`)
}

export function getUsersDataDownload(accountId, employeeReportParams: IEmployeesReportParameters) {
	const limitOnAPI = employeeReportParams.Limit === -1 ? null : employeeReportParams.Limit
	employeeReportParams.Limit = limitOnAPI
	return axiosInstance.post(`/reporting/${accountId}/export_users`, employeeReportParams)
}

export function getJobsDataDownload(accountId, jobReportParams: IJobReportParameters) {
	return axiosInstance.post(`/reporting/${accountId}/export_jobs`, jobReportParams)
}

export function getDevicesDataDownload(accountId, deviceReportParams: IDeviceReportParameters) {
	return axiosInstance.post(`/reporting/${accountId}/export_devices`, deviceReportParams)
}

export function getLeaveRequestsDataDownload(accountId, timeoffFilter: ITimeoffFilter, page, limit) {
	return axiosInstance.post(`/reporting/${accountId}/export_timeoffs_v1?page=${page}&limit=${limit}`, timeoffFilter)
}

export function getUserLeaveRequestsDataDownload(userId, startDate, endDate, search, timeoffStatusList) {
	return axiosInstance.post(`/reporting/export_user_timeoffs?userId=${userId}&startDate=${startDate}&endDate=${endDate}&search=${search}`, timeoffStatusList)
}

export function getUserRecordsDataDownload(userId, startDate, endDate) {
	return axiosInstance.post(`/reporting/user_records?userId=${userId}&startDate=${startDate}&endDate=${endDate}`)
}

export function getClientsDataDownload(accountId, levelIdFilter, page, limit, search, clientActiveStatus: EClientActiveStatus | "all") {
	return axiosInstance.get(`/reporting/${accountId}/export_clients?levelIdFilter=${levelIdFilter}&page=${page}&limit=${limit}&search=${search}&clientActiveStatus=${clientActiveStatus === "all" ? "" : clientActiveStatus}`)
}

export function getJobDashboardChartData(jobId, referenceDate, isToday, utcTime, showChartTypes: EJobDashboardCircleChartType[]) {
	return axiosInstance.post(`/dashboard/job_dashboard_chart_data?
		jobId=${jobId}&
		referenceDate=${utils.escapeDateTime(referenceDate)}&
		isToday=${isToday}&
		utcTime=${utils.escapeDateTime(utcTime)}`,
		showChartTypes
	)
}

export function getJobDashboardWorkEmployees(jobId, isToday, referenceDate, utcTime, showOnlyTodayWorking, offset, limit) {
	return axiosInstance.get(`/dashboard/job_dashboard_work_employees?
		jobId=${jobId}&
		isToday=${isToday}&
		referenceDate=${utils.escapeDateTime(referenceDate)}&
		utcTime=${utils.escapeDateTime(utcTime)}&
		showOnlyTodayWorking=${showOnlyTodayWorking}&
		page=${offset / limit}&
		limit=${limit}`
	)
}

export function getJobDashboardOTEmployees(type, jobId, referenceDate, utcTime, offset, limit) {
	return axiosInstance.get(`/dashboard/job_dashboard_ot_employees?
		type=${type}&
		jobId=${jobId}&
		referenceDate=${utils.escapeDateTime(referenceDate)}&
		utcTime=${utils.escapeDateTime(utcTime)}&
		page=${offset / limit}&
		limit=${limit}`
	)
}

export function getFilters(userId: string) {
	return axiosInstance.get(`/users/${userId}/filters`)
}

export function addUserFilter(userId: string, userFilter: IUserFilter) {
	return axiosInstance.post(`/users/${userId}/user_filter`, userFilter)
}

export function updateUserFilter(userId: string, userFilter: IUserFilter) {
	return axiosInstance.put(`/users/${userId}/user_filter`, userFilter)
}

export function deleteUserFilter(userId: string, filterId: string) {
	return axiosInstance.delete(`/users/${userId}/user_filter/${filterId}`)
}

export function setSelectedUserFilter(userId: string, filterId: string) {
	return axiosInstance.post(`/users/${userId}/set_user_filter?filterId=${filterId}`)
}

export function addTimecardUserFilter(userId: string, userFilter: IUserFilter) {
	return axiosInstance.post(`/users/${userId}/timecard_user_filter`, userFilter)
}

export function updateTimecardUserFilter(userId: string, userFilter: IUserFilter) {
	return axiosInstance.put(`/users/${userId}/timecard_user_filter`, userFilter)
}

export function deleteTimecardUserFilter(userId: string, filterId: string) {
	return axiosInstance.delete(`/users/${userId}/timecard_user_filter/${filterId}`)
}

export function setSelectedTimecardUserFilter(userId: string, filterId: string) {
	return axiosInstance.post(`/users/${userId}/set_timecard_user_filter?filterId=${filterId}`)
}

export function addResourcePlannerScheduleFilter(userId: string, userFilter: IResourcePlannerScheduleFilter) {
	return axiosInstance.post(`/users/${userId}/resource_planner_schedule_filter`, userFilter)
}

export function updateResourcePlannerScheduleFilter(userId: string, userFilter: IResourcePlannerScheduleFilter) {
	return axiosInstance.put(`/users/${userId}/resource_planner_schedule_filter`, userFilter)
}

export function deleteResourcePlannerScheduleFilter(userId: string, filterId: string) {
	return axiosInstance.delete(`/users/${userId}/resource_planner_schedule_filter/${filterId}`)
}

export function setSelectedResourcePlannerScheduleFilter(userId: string, filterId: string) {
	return axiosInstance.post(`/users/${userId}/set_resource_planner_schedule_filter?filterId=${filterId}`)
}

export function addJobFilter(userId: string, jobFilter: IJobFilter) {
	return axiosInstance.post(`/users/${userId}/job_filter`, jobFilter)
}

export function updateJobFilter(userId: string, jobFilter: IJobFilter) {
	return axiosInstance.put(`/users/${userId}/job_filter`, jobFilter)
}

export function deleteJobFilter(userId: string, filterId: string) {
	return axiosInstance.delete(`/users/${userId}/job_filter/${filterId}`)
}

export function setSelectedJobFilter(userId: string, filterId: string) {
	return axiosInstance.post(`/users/${userId}/set_job_filter?filterId=${filterId}`)
}

export function addClientJobFilter(userId: string, clientJobFilter: IJobFilter) {
	return axiosInstance.post(`/users/${userId}/client_job_filter`, clientJobFilter)
}

export function updateClientJobFilter(userId: string, clientJobFilter: IJobFilter) {
	return axiosInstance.put(`/users/${userId}/client_job_filter`, clientJobFilter)
}

export function deleteClientJobFilter(userId: string, filterId: string) {
	return axiosInstance.delete(`/users/${userId}/client_job_filter/${filterId}`)
}

export function setSelectedClientJobFilter(userId: string, filterId: string) {
	return axiosInstance.post(`/users/${userId}/set_client_job_filter?filterId=${filterId}`)
}

export function addTimeoffFilter(userId: string, timeoffFilter: ITimeoffFilter) {
	return axiosInstance.post(`/users/${userId}/user_timeoff_filter`, timeoffFilter)
}

export function updateTimeoffFilter(userId: string, timeoffFilter: ITimeoffFilter) {
	return axiosInstance.put(`/users/${userId}/user_timeoff_filter`, timeoffFilter)
}

export function deleteTimeoffFilter(userId: string, filterId: string) {
	return axiosInstance.delete(`/users/${userId}/user_timeoff_filter/${filterId}`)
}

export function setSelectedTimeoffFilter(userId: string, filterId: string) {
	return axiosInstance.post(`/users/${userId}/set_user_timeoff_filter?filterId=${filterId}`)
}

export function getDayPayrollLockStatus(accountId, payrollPayGroupData: IPayrollPayGroupData, day, payrollType) {
	return axiosInstance.post(`/accounts/${accountId}/get_day_lock_status_v1?
		payrollType=${payrollType}&
		day=${utils.escapeDateTime(day)}`,
		payrollPayGroupData
	)
}

export function sendUserWelcomeEmail(userId: string) {
	return axiosInstance.get(`/users/${userId}/send_welcome_email`)
}

export function verifyEmail(email: string, token: string) {
	return axiosInstance.get(`/users/verify_email?email=${email}&token=${token}`)
}

export function setupPaymentIntent(accountId: string) {
	return axiosInstance.get(`/stripe/get_payment_method_intent/${accountId}`)
}

export function getPaymentMethods(accountId: string) {
	return axiosInstance.get(`/stripe/get_payment_methods/${accountId}`)
}

export function removePaymentMethod(paymentMethodId: string) {
	return axiosInstance.get(`/stripe/remove_payment_method/${paymentMethodId}`)
}

export function getInvoices(accountId: string) {
	return axiosInstance.get(`/stripe/get_invoices/${accountId}`)
}

export function getFavorites(favoriteParams: IFavoriteParams) {
	return axiosInstance.post("/users/get_favorites", favoriteParams)
}

export function addToFavorite(favoriteItem: IFavoriteDocument) {
	return axiosInstance.post("/users/add_favorite", favoriteItem)
}

export function removeFromFavorite(objectId: string) {
	return axiosInstance.delete(`/users/delete_favorite/${objectId}`)
}

export function loadHtml(url: string) {
	return axiosInstance.get(`/loader?url=${encodeURIComponent(url)}`)
}

export function getUserWorkflows(userId: string) {
	return axiosInstance.get(`/users/${userId}/workflows`)
}

export function getUserWorkflow(userId: string, workflowId: string) {
	return axiosInstance.get(`/users/${userId}/workflows/${workflowId}`)
}

export function closeTask(taskId: string) {
	return axiosInstance.get(`/tasks/${taskId}/close`)
}

export function getClientByExternalId(externalId: string) {
	return axiosInstance.get(`clients/get_by_externalId/${externalId}`)
}
export function validatePhone(phoneId: string, accountId: string) {
	return axiosInstance.get(`/twilio/valid_phone?phone=${encodeURIComponent(phoneId)}&accountId=${accountId}`)
}

export function sendAppDownloadSMS(taskId: string, phoneId: string) {
	return axiosInstance.get(`/tasks/${taskId}/send_mobile_link?phoneNumber=${encodeURIComponent(phoneId)}`)
}

export function getAccountLatestStats(accountId: string) {
	return axiosInstance.get(`/account_stats/latest?accountId=${accountId}`)
}

export function getAccountsConfig() {
	return axiosInstance.get("/accounts/config")
}

export function getAccrualBalanceReport(accountId, timeoffFilter: ITimeoffFilter) {
	return axiosInstance.post(`/reporting/${accountId}/employee_accrual_balance_report_v1`, timeoffFilter)
}

export function initDevices(deviceIds) {
	return axiosInstance.post("/devices/init", deviceIds)
}

export function addEmployeeFile(userId: string, file: IFileDocument) {
	return axiosInstance.post(`/employeefiles/${userId}/add_time_file`, file)
}

export function getEmployeeFiles(userId: string, search: IEmployeeFileSearchParams) {
	return axiosInstance.post(`/users/${userId}/search_files`, search)
}

export function deleteEmployeeFile(employeeFileDocumentId: string) {
	return axiosInstance.delete(`/employeefiles/${employeeFileDocumentId}`)
}

export function downloadEmployeeFile(employeeFileDocumentId: string) {
	return axiosInstance.get(`/employeefiles/download/${employeeFileDocumentId}`)
}

export function updateNoteType(accountId: string, noteType: INoteType) {
	return axiosInstance.put(`/accounthrissettings/${accountId}/update_note_type`, noteType)
}

export function addNoteType(accountId: string, noteType: INoteType) {
	return axiosInstance.post(`/accounthrissettings/${accountId}/add_note_type`, noteType)
}

export function deleteNoteType(accountId: string, noteTypeId: string) {
	return axiosInstance.delete(`/accounthrissettings/${accountId}/delete_note_type?id=${noteTypeId}`)
}

export function allowDeleteOrInactiveJobs(jobIds: string[]) {
	return axiosInstance.post(`/jobs/allow_delete_or_inactive_jobs`, jobIds)
}

export function allowDeleteOrInactivePhases(phaseIds: string[]) {
	return axiosInstance.post(`/jobs/allow_delete_or_inactive_phases`, phaseIds)
}

export function recalcFailedAccrual(payrollStatusDocIds: string[]) {
	return axiosInstance.post(`/accounts/recals_failed_accruals`, payrollStatusDocIds)

}

export function updateJobUserMofifiedFlag(jobId: string, userModified: boolean) {
	return axiosInstance.post(`/jobs/${jobId}/update_user_modified?userModified=${userModified}`)
}

export function getNewCustomReport(reportDocId: string, asyncSessionId: string, newCustomReportParameters: INewCustomReportParameters) {
	return axiosInstance.post(`/reporting/new_custom_report?reportDocId=${reportDocId}&asyncSessionId=${asyncSessionId}`, newCustomReportParameters)
}

export function getJobImages(jobId: string) {
	return axiosInstance.get(`/jobs/${jobId}/images`)
}

export function addJobImage(jobId: string, file: File) {
	let thisAxiosInstance = new AxiosInstance().instance
	setProxy()
	let formData = new FormData()
	formData.append("File", file, file.name)

	thisAxiosInstance.interceptors.request.use(config => {
		config.data = formData
		config.headers["Content-Type"] = "multipart/form-data"
		return config
	})
	return thisAxiosInstance.post(`/jobs/${jobId}/images`)
}

export function deleteJobImages(jobId: string, imageIds: string[]) {
	return axiosInstance.delete(`/jobs/${jobId}/images`, { data: imageIds })
}

export function declineCandidateOnJob(userId: string, jobId: string, declineNote: string) {
	return axiosInstance.post(`/users/${userId}/jobs/${jobId}/decline`, { Note: declineNote })
}

export function declineCandidateOnShift(userId: string, shiftId: string, declineNote: string) {
	return axiosInstance.post(`/schedules/${shiftId}/decline_shift/${userId}?note=${declineNote}`)
}

export function getGigPools() {
	return axiosInstance.get(`/gigpool`)
}

export function addGigPool(doc: IGigPoolDocument) {
	return axiosInstance.post(`/gigpool`, doc)
}

export function updateGigPool(doc: IGigPoolDocument) {
	return axiosInstance.put(`/gigpool`, doc)
}

export function getMessageGroups(objectId: string) {
	return axiosInstance.get(`/message_group?objectId=${objectId}`)
}

export function getGigPoolMembers(id: string) {
	return axiosInstance.get(`/gigpool/${id}/members`)
}

export function sendGroupMessage(doc: IMessageGroupSend) {
	return axiosInstance.post(`/message_group/sendmessage`, doc)
}

export function getSuggestedMessage(prompt: ISuggestedMessagePrompt) {
	return axiosInstance.post(`/message_group/suggested`, prompt)
}

export function getScheduleAssignCandidates(
	shiftStartDateTime,
	offset,
	limit,
	filterText,
	levelIdFilter = "",
	filterByDirectReports,
	filterByDefaultHierarchy,
	limitToShiftHierarchy,
	limitToShiftPosition,
	shiftBasePositionId
) {
	return axiosInstance.get(
		"/schedules/assign_candidates?" +
		"shiftStartDateTime=" + shiftStartDateTime +
		"&levelIdFilter=" + levelIdFilter +
		"&page=" + offset / limit +
		"&limit=" + limit +
		"&search=" + filterText +
		"&filterByDirectReports=" + filterByDirectReports +
		"&filterByDefaultHierarchy=" + filterByDefaultHierarchy +
		"&limitToShiftHierarchy=" + limitToShiftHierarchy +
		"&limitToShiftPosition=" + limitToShiftPosition +
		"&shiftBasePositionId=" + shiftBasePositionId
	)
}

export function getEmployeeTypeData(employeeIds: string[]) {
	return axiosInstance.post("/users/get_employee_type_data", employeeIds)
}

export function getEmployeeTimecardReport(accountId: string, userId: string, startDate: string, endDate: string, reportDate: string, includePunchPhoto: boolean) {
	return axiosInstance.get(
		"/reportingv1/" +
		accountId +
		"/employee_timecard_report?userId=" +
		userId +
		"&startDate=" +
		encodeURIComponent(startDate) +
		"&endDate=" +
		encodeURIComponent(endDate) +
		"&reportDate=" +
		encodeURIComponent(reportDate) +
		"&includePunchPhoto=" +
		includePunchPhoto
	)
}

export function getBreadCrumbData(objectId: string, startDate: string, endDate: string) {
	return axiosInstance.get(`/breadcrumb/${objectId}?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`)
}

export function getJobClockDevices(jobId) {
	return axiosInstance.get(`/jobs/${jobId}/devices`)
}

export function getJobEmployees(jobId: string, defaultJobEmployees: boolean, assignedEmployees: boolean, floatEmployees: boolean, page, limit) {
	return axiosInstance.get(
		`/jobs/${jobId}/get_job_employees?
		defaultJobEmployees=${defaultJobEmployees}
		&assignedEmployees=${assignedEmployees}
		&floatEmployees=${floatEmployees}
		&page=${page}
		&limit=${limit}`
	)
}

export function setUserLanguage(userId: string, language: ELanguage) {
	return axiosInstance.post(`/users/${userId}/language`, { data: language })
}

export function getJobIANATimezoneId(jobId: string) {
	return axiosInstance.get(`/jobs/${jobId}/ianatimezoneid`)
}

export function getExceptionsReport(accountId: string, referenceDate: string, isPayrollPage: boolean, hierarchyItemId: string, filterByDirectReports: boolean, filterByPunchHierarchy: boolean, filterByDefaultHierarchy: boolean, viewPayrollByJob: boolean, filterObj: IPunchExceptionFilterDto) {
	return axiosInstance.post(`/reporting/exceptions_report?accountId=${accountId}&referenceDate=${encodeURIComponent(referenceDate)}&isPayrollPage=${isPayrollPage}&hierarchyItemId=${hierarchyItemId}&filterByDirectReports=${filterByDirectReports}&filterByPunchHierarchy=${filterByPunchHierarchy}&filterByDefaultHierarchy=${filterByDefaultHierarchy}&viewPayrollByJob=${viewPayrollByJob}`, filterObj)
}

export function trendSearch(hierarchyId, startDate, endDate, scope) {
	return axiosInstance.get(`/trends/search?		
		startDate=${utils.escapeDateTime(startDate)}&endDate=${utils.escapeDateTime(endDate)}&hierarchyId=${hierarchyId}&scope=${scope}`
	)
}

export function checkIVRPhoneNumberValidate(accountId: string, phoneNumber: string) {
	return axiosInstance.get(`/twilio_ivr/check_phone_number_validate?accountId=${accountId}&phoneNumber=${phoneNumber}`)
}

export function getUserEffectiveDates(userId: string) {
	return axiosInstance.get(`/users/${userId}/user_effective_dates`)
}
